import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import Header from '../../components/Header';
import GeneralInfo from '../../components/dashboard/GeneralInfo';
import Recommendations from '../../components/dashboard/Recommendations';
import DayPlans from '../../components/dashboard/DayPlans';
import ShoppingList from '../../components/dashboard/ShoppingList';
import api from '../../services/api';
import nutritionService from '../../services/nutritionService';

const PlanView = () => {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shoppingList, setShoppingList] = useState([]);

  useEffect(() => {
    const fetchPlanDetails = async () => {
      try {
        // V2.0: Utiliser l'API avec les recettes peuplées
        const [planData, shoppingData] = await Promise.all([
          api.get(`/admin/weekplans/${id}`),
          nutritionService.getShoppingListFromPlan(id).catch(err => {
            console.warn('Shopping list API error:', err.message);
            return null;
          })
        ]);

        // Normaliser client -> user pour compatibilité
        if (planData.client && !planData.user) {
          planData.user = planData.client;
        }

        setPlan(planData);

        // Utiliser la liste de courses de l'API si disponible
        if (shoppingData?.items) {
          setShoppingList(shoppingData.items);
        } else {
          // Fallback: générer localement depuis les ingrédients du plan
          const allIngredients = [];
          for (const dayPlan of planData.dayPlans || []) {
            for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
              const meal = dayPlan.meals?.[mealKey];
              if (meal?.ingredients) {
                allIngredients.push(...meal.ingredients);
              }
            }
          }

          // Agréger les ingrédients
          const aggregated = {};
          for (const ing of allIngredients) {
            const key = ing.name || ing.ciqual_code;
            if (aggregated[key]) {
              aggregated[key].quantity += ing.quantity || 0;
            } else {
              aggregated[key] = { ...ing };
            }
          }

          setShoppingList(Object.values(aggregated).map(ing => ({
            ingredient: ing.name,
            quantity: Math.round(ing.quantity || 0),
            unit: ing.unit || 'g'
          })));
        }

        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la récupération des données :', err.message);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPlanDetails();
  }, [id]);
  

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Header />
      <Box sx={{ px: { xs: 2, sm: 6 }, pt: 3, pb: 6, maxWidth: '880px', width: { xs: '-webkit-fill-available', md: '100%' }, margin: '0 auto' }}>
        <GeneralInfo
          user={plan.user}
          dietaryPreferences={plan.user?.dietaryPreferences}
          goals={plan.user?.goals}
          allergies={plan.user?.allergies}
          summary={plan.summary}
          targetMacros={plan.user?.targetMacros}
          planId={plan._id}
        />
        <Recommendations recommendations={plan.recommendations} />
        <DayPlans dayPlans={plan.dayPlans} targetMacros={plan.user?.targetMacros} />
        <ShoppingList shoppingList={shoppingList} />
      </Box>
    </>
  );
};

export default PlanView;
